import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const WishlistContext = createContext(null);
// Non-sensitive data only (public catalog references) — localStorage is intentional.
const STORAGE_KEY = "monolith.wishlist.v1";

export const WishlistProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const has = useCallback((id) => items.some((i) => i.id === id), [items]);

    const toggle = useCallback((product) => {
        setItems((prev) => {
            if (prev.some((i) => i.id === product.id)) {
                return prev.filter((i) => i.id !== product.id);
            }
            return [
                ...prev,
                {
                    id: product.id,
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    category: product.category,
                    image: product.images?.[0],
                },
            ];
        });
    }, []);

    const remove = useCallback(
        (id) => setItems((prev) => prev.filter((i) => i.id !== id)),
        []
    );

    const value = useMemo(
        () => ({ items, has, toggle, remove, count: items.length }),
        [items, has, toggle, remove]
    );

    return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
    const ctx = useContext(WishlistContext);
    if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
    return ctx;
};
